import { useState } from "react";
import { Plus, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReportPanel } from "@/components/ReportPanel";
import { EntryDialog } from "@/components/EntryDialog";
import { BulkTransferDialog } from "@/components/BulkTransferDialog";
import { ENTRY_KINDS } from "@/lib/entries";
import { can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PAGES = {
  vi: { title: "VI Virtual Account", hint: "Opening + Credit − DSE transfer/debit = Closing virtual balance", color: "#E60000", report: "vi-ledger", actions: ["vi", "vi-payout"], filters: ["distributor_id"] },
  dse: { title: "DSE Balance Ledger", hint: "Opening + Balance given − Actual cash/UPI recovery = Outstanding", color: "#D97706", report: "dse-ledger", actions: ["dse-transfer", "dse-recovery", "dse-hisab", "vtop-transfer", "vtop-recharge", "vtop-adjust", "retailer-payment", "dse-adjustment", "razorpay-receipt"], filters: ["distributor_id", "dse_id"] },
  fl: { title: "FL Advance & Commission", hint: "Opening + Advance − Commission adjustment − Cash/UPI recovery = Pending advance", color: "#0284C7", report: "fl-report", actions: ["fl-advance", "fl-commission", "fl-adjustment", "fl-recovery", "fl-payment"], filters: ["distributor_id", "fl_id"] },
  cash: { title: "Cash / Bank / UPI", hint: "Actual paison ka ledger — receipts, payments aur contra transfer", color: "#059669", report: "cash-bank", actions: ["cash"], filters: ["distributor_id", "account", "fund_account_id"] },
};

export default function LedgerPage({ ledger }) {
  const p = PAGES[ledger];
  const { user } = useAuth();
  const [kind, setKind] = useState(null);
  const [bulk, setBulk] = useState(false);
  const actions = p.actions.filter((k) => can(user, ENTRY_KINDS[k].perm));
  return (
    <div className="space-y-6" key={ledger}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="h-1 w-14 rounded-full mb-3" style={{ background: p.color }} />
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" data-testid={`${ledger}-page-title`}>{p.title}</h1>
          <span className="hint-tag mt-2">{p.hint}</span>
          {p.filters.some((f) => f === "dse_id" || f === "fl_id") && (
            <p className="text-[11px] text-slate-500 mt-1.5">Entry-wise ledger aur Reverse option ke liye filter se ek {ledger.toUpperCase()} select karein.</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {actions.map((k) => (
            <Button key={k} size="sm" onClick={() => setKind(k)} style={{ background: p.color }} className="text-white hover:opacity-90" data-testid={`${ledger}-action-${k}`}>
              <Plus className="h-4 w-4" /> {ENTRY_KINDS[k].title}
            </Button>
          ))}
          {ledger === "dse" && can(user, "import:preview") && (
            <Button size="sm" variant="outline" onClick={() => setBulk(true)} className="border-[#D97706] text-[#b45309]" data-testid="dse-bulk-upload-btn">
              <UploadCloud className="h-4 w-4" /> Bulk Excel Upload (Transfer)
            </Button>
          )}
        </div>
      </div>
      <ReportPanel reportKey={p.report} filters={p.filters} />
      {ledger === "dse" && (
        <div className="pt-4 border-t border-slate-200" data-testid="vtop-section">
          <ReportPanel reportKey="vtop-transfer" filters={["distributor_id", "dse_id", "retailer_id"]} />
        </div>
      )}
      {ledger === "dse" && (
        <div className="pt-4 border-t border-slate-200" data-testid="retailer-ledger-section">
          <ReportPanel reportKey="retailer-ledger" filters={["distributor_id", "retailer_id"]} />
        </div>
      )}
      {ledger === "dse" && (
        <div className="pt-4 border-t border-slate-200" data-testid="dse-wallet-section">
          <ReportPanel reportKey="dse-wallet" filters={["distributor_id", "dse_id"]} />
        </div>
      )}
      {ledger === "dse" && (
        <div className="pt-4 border-t border-slate-200" data-testid="razorpay-section">
          <ReportPanel reportKey="razorpay" filters={["distributor_id"]} />
        </div>
      )}
      {kind && <EntryDialog kind={kind} open={!!kind} onOpenChange={(o) => !o && setKind(null)} />}
      {ledger === "dse" && <BulkTransferDialog open={bulk} onOpenChange={setBulk} />}
    </div>
  );
}
